import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { FirebaseReading } from '../../types/firebase';
import { formatChartTime } from '../../utils/helpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type ChartMode = 'climate' | 'wind' | 'light-rain';

interface DataChartProps {
  data: FirebaseReading[];
  mode: ChartMode;
}

export const DataChart: React.FC<DataChartProps> = ({ data, mode }) => {
  const initialRenderRef = useRef(true);

  // Safety check
  if (!data || data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center" 
        style={{ 
          height: '500px', 
          color: '#9ca3af',
          backgroundColor: 'transparent'
        }}
      >
        <p>No data available for selected time range</p>
      </div>
    );
  }

  // Filter out readings with invalid timestamps
  const validData = data.filter((reading) => {
    if (!reading || !reading.timestamp) return false;
    const timestamp = reading.timestamp;
    let timestampNum: number;

    if (typeof timestamp === 'number') {
      timestampNum = timestamp;
    } else if (typeof timestamp === 'string') {
      if (/^\d+$/.test(timestamp.trim())) {
        timestampNum = parseInt(timestamp.trim(), 10);
      } else {
        timestampNum = new Date(timestamp).getTime();
      }
    } else {
      return false;
    }

    // Check if timestamp is valid (not NaN, not 0, and reasonable range)
    return !isNaN(timestampNum) && 
           timestampNum > 0 && 
           timestampNum > 946684800000 && // After year 2000
           timestampNum < 4102444800000; // Before year 2100
  });

  // Create labels from valid data only
  const labels = validData
    .map((reading) => formatChartTime(reading.timestamp))
    .filter(Boolean);

  // Evenly thin labels: target ~8 ticks, always show first/last
  const targetTicks = 8;
  const step = Math.max(1, Math.floor(labels.length / targetTicks));

  const buildGradient = (color: string) => {
    return (context: { chart: ChartJS }) => {
      const { chart } = context;
      const { ctx, chartArea } = chart;
      if (!chartArea) {
        return color;
      }
      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      gradient.addColorStop(0, `${color}33`); // light tint
      gradient.addColorStop(1, `${color}05`); // fade to transparent
      return gradient;
    };
  };

  const getDatasets = () => {
    const baseConfig = {
      tension: 0.38,
      fill: true,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHitRadius: 12,
      borderWidth: 2.2,
    } as const;

    // Use validData instead of data to ensure labels and datasets match
    switch (mode) {
      case 'climate':
        return [
          {
            label: 'Temperature (°C)',
            data: validData.map((d) => d.temperature ?? null),
            borderColor: '#ff6b35',
            backgroundColor: buildGradient('#ff6b35'),
            yAxisID: 'y',
            ...baseConfig,
          },
          {
            label: 'Humidity (%)',
            data: validData.map((d) => d.humidity ?? null),
            borderColor: '#00d4ff',
            backgroundColor: buildGradient('#00d4ff'),
            yAxisID: 'y1',
            ...baseConfig,
          },
          {
            label: 'Pressure (hPa)',
            data: validData.map((d) => d.pressure ?? null),
            borderColor: '#bc8cff',
            backgroundColor: buildGradient('#bc8cff'),
            yAxisID: 'y2',
            ...baseConfig,
          },
          {
            label: 'Wind Speed (m/s)',
            data: validData.map((d) => d.windSpeed ?? null),
            borderColor: '#7c3aed',
            backgroundColor: buildGradient('#7c3aed'),
            yAxisID: 'y3',
            ...baseConfig,
          },
        ];
      case 'wind':
        return [
          {
            label: 'Wind Speed (m/s)',
            data: validData.map((d) => d.windSpeed ?? null),
            borderColor: '#7c3aed',
            backgroundColor: buildGradient('#7c3aed'),
            yAxisID: 'y',
            ...baseConfig,
          },
          {
            label: 'Wind Direction (°)',
            data: validData.map((d) => d.windDirection ?? null),
            borderColor: '#06b6d4',
            backgroundColor: buildGradient('#06b6d4'),
            yAxisID: 'y1',
            ...baseConfig,
          },
        ];
      case 'light-rain':
        return [
          {
            label: 'Light (%)',
            data: validData.map((d) => d.light ?? null),
            borderColor: '#ffd700',
            backgroundColor: buildGradient('#ffd700'),
            yAxisID: 'y',
            ...baseConfig,
          },
          {
            label: 'Rain (%)',
            data: validData.map((d) => d.rain ?? null),
            borderColor: '#4a90e2',
            backgroundColor: buildGradient('#4a90e2'),
            yAxisID: 'y1',
            ...baseConfig,
          },
        ];
    }
  };

  // If no valid data after filtering, show empty state
  if (validData.length === 0 || labels.length === 0) {
    return (
      <div 
        className="flex items-center justify-center" 
        style={{ 
          height: '500px', 
          color: '#9ca3af',
          backgroundColor: 'transparent'
        }}
      >
        <p>No valid data available for selected time range</p>
      </div>
    );
  }

  const filteredDatasets = getDatasets().map((dataset) => ({
    ...dataset,
    data: dataset.data.map((value) => (value === null || value === undefined ? null : value)),
  }));

  const chartData = {
    labels,
    datasets: filteredDatasets,
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 12,
        right: 16,
        bottom: 12,
        left: 12,
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: "'Space Grotesk', sans-serif",
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15, 17, 20, 0.96)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.12)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 10,
        boxPadding: 4,
        displayColors: true,
        titleFont: {
          family: "'Space Grotesk', sans-serif",
          weight: 600,
        },
        bodyFont: {
          family: "'Space Grotesk', sans-serif",
        },
      },
    },
    animation: initialRenderRef.current
      ? {
          duration: 1200,
          easing: 'easeOutQuart',
          delay: (ctx) => {
            if (ctx.type === 'data' && ctx.dataIndex !== undefined) {
              return ctx.dataIndex * 8;
            }
            return 0;
          },
        }
      : { duration: 0 },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time',
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            family: "'Space Grotesk', sans-serif",
            size: 12,
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.06)',
          tickColor: 'rgba(255, 255, 255, 0.1)',
        },
        border: {
          display: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          maxRotation: 45,
          minRotation: 45,
          callback: (_value, index) => {
            if (index === 0 || index === labels.length - 1) {
              return labels[index];
            }
            return index % step === 0 ? labels[index] : '';
          },
          font: {
            family: "'Space Grotesk', sans-serif",
            size: 11,
          },
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: {
          color: 'rgba(255, 255, 255, 0.06)',
        },
        border: {
          display: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            family: "'Space Grotesk', sans-serif",
            size: 11,
          },
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
          color: 'rgba(255, 255, 255, 0.04)',
        },
        border: {
          display: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            family: "'Space Grotesk', sans-serif",
            size: 11,
          },
        },
      },
      y2: {
        type: 'linear',
        display: mode === 'climate',
        position: 'right',
        grid: {
          drawOnChartArea: false,
          color: 'rgba(255, 255, 255, 0.04)',
        },
        border: {
          display: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            family: "'Space Grotesk', sans-serif",
            size: 11,
          },
        },
      },
      y3: {
        type: 'linear',
        display: mode === 'climate',
        position: 'right',
        grid: {
          drawOnChartArea: false,
          color: 'rgba(255, 255, 255, 0.04)',
        },
        border: {
          display: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            family: "'Space Grotesk', sans-serif",
            size: 11,
          },
        },
      },
    },
  };

  useEffect(() => {
    // After first paint with data, disable animation for subsequent renders
    if (initialRenderRef.current) {
      const timer = setTimeout(() => {
        initialRenderRef.current = false;
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [mode, labels.length]);

  return (
    <div 
      className="w-full" 
      style={{ 
        height: '500px', 
        background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.08))',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '16px',
        position: 'relative',
        zIndex: 1,
        padding: '12px'
      }}
    >
      <Line 
        data={chartData} 
        options={options}
      />
    </div>
  );
};
